// OAuth interception removed — /liberacao route deleted

import React, { useEffect, Suspense, useMemo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { createOptimizedQueryClient } from "@/lib/queryClient";
import { cacheService } from "@/services/cacheService";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";
import { AuthProvider } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { HelmetProvider } from 'react-helmet-async';
import { FeatureDetector } from "@/components/ui/feature-detector";
import { SafeLoading } from "@/components/ui/safe-loading";
import { logger } from "@/utils/logger";
import NotFound from "./pages/NotFound";
import SupplierRegistration from "./pages/SupplierRegistration";
const PricingPage = React.lazy(() => import("./pages/PricingPage"));
import { CustomDomainWrapper } from "./components/CustomDomainWrapper";
import WhiteLabelNoIndex from "@/components/WhiteLabelNoIndex";
import WhiteLabelBrandingProvider from "@/components/WhiteLabelBrandingProvider";
import WhiteLabelLanding from "@/pages/WhiteLabelLanding";
import { isWhiteLabelHost } from "@/utils/appUrl";
import { ClientPortalProvider } from "./context/ClientPortalContext";
import FloatingCartButton from "./components/supplier-menu/FloatingCartButton";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import { ViewTracker } from "./components/ViewTracker";

// Lazy load client portal pages
const ClientPortalLoginPage = React.lazy(() => import("./pages/ClientPortalLoginPage"));
const ClientPortalEventsPage = React.lazy(() => import("./pages/ClientPortalEventsPage"));
const PaymentRedirectPage = React.lazy(() => import("./pages/PaymentRedirectPage"));

const ShareRedirectPage = React.lazy(() => import("./pages/ShareRedirectPage"));
const EventPassportPage = React.lazy(() => import("./pages/EventPassportPage"));
const AdditionalChargePage = React.lazy(() => import("./pages/AdditionalChargePage"));
const InstallmentPaymentPage = React.lazy(() => import("./pages/InstallmentPaymentPage"));

// Lazy load LojaPage (antiga UnifiedHomePage)
const LojaPage = React.lazy(() => import("./pages/LojaPage"));

// Blog pages
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const AgendarReuniaoPage = React.lazy(() => import("./pages/AgendarReuniaoPage"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));

import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import UpdatePasswordPage from "./pages/UpdatePasswordPage";
import NovaSenhaPage from "./pages/NovaSenhaPage";
import AcceptCollaboratorInvite from "./pages/AcceptCollaboratorInvite";
import InfosFesta from "./pages/InfosFesta";
import SobreNos from "./pages/SobreNos";
import ProblemaSolucao from "./pages/ProblemaSolucao";
const SolucaoPage = React.lazy(() => import("./pages/SolucaoPage"));
const SolucoesPage = React.lazy(() => import("./pages/SolucoesPage"));




import SupplierProtectedRoute from "./components/SupplierProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import PartnerProtectedRoute from "./components/PartnerProtectedRoute";
const PartnerDashboard = React.lazy(() => import("./pages/PartnerDashboard"));
const DemoParceiraLogin = React.lazy(() => import("./pages/DemoParceiraLogin"));

// Lazy load heavy dashboard components for better performance
const SupplierDashboard = React.lazy(() => import("./pages/SupplierDashboard"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));


// Lazy load supplier menu and store pages
const SupplierMenuPage = React.lazy(() => import("./pages/SupplierMenuPage"));
const SupplierStorePage = React.lazy(() => import("./pages/SupplierStorePage"));


const CheckoutChangesPage = React.lazy(() => import("./pages/CheckoutChangesPage"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const OrderConfirmationPage = React.lazy(() => import("./pages/OrderConfirmationPage"));
const GoogleCalendarCallback = React.lazy(() => import("./pages/auth/GoogleCalendarCallback"));
const ContractSignaturePage = React.lazy(() => import("./pages/ContractSignaturePage"));
const PaymentSuccessPage = React.lazy(() => import("./pages/PaymentSuccessPage"));

import PagBankCallbackPage from "./pages/PagBankCallbackPage";
import TermosDeUso from "./pages/TermosDeUso";
import PoliticaDePrivacidade from "./pages/PoliticaDePrivacidade";
const ExemploCardapioDigital = React.lazy(() => import("./pages/ExemploCardapioDigital"));
const MateriaisGratuitos = React.lazy(() => import("./pages/MateriaisGratuitos"));
const CalculadoraPrecoInsumos = React.lazy(() => import("./pages/CalculadoraPrecoInsumos"));
const CalculadoraFreteEventos = React.lazy(() => import("./pages/CalculadoraFreteEventos"));
const CalculadoraBebidasEventos = React.lazy(() => import("./pages/CalculadoraBebidasEventos"));
const ModeloContratoBuffet = React.lazy(() => import("./pages/ModeloContratoBuffet"));
const ReformaTributariaBuffet = React.lazy(() => import("./pages/ReformaTributariaBuffet"));
const UnsubscribePage = React.lazy(() => import("./pages/UnsubscribePage"));
const LinksPage = React.lazy(() => import("./pages/LinksPage"));
const LinksAdminPage = React.lazy(() => import("./pages/LinksAdminPage"));

// Create optimized QueryClient instance with enhanced caching
const queryClient = createOptimizedQueryClient();

// Initialize cache service
cacheService.initialize(queryClient);

// Analytics tracking component
const AnalyticsTracker = () => {
  const location = useLocation();
  const { trackPageView, trackEvent } = useAnalytics();
  
  // Detect if we are in a white-label domain to reduce tracking overhead and external script errors
  const isWhiteLabel = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h.includes("amecardapio.com");
  }, []);

  useEffect(() => {
    // Skip tracking for common system events on white-label if preferred, 
    // or just ensure logs are cleaner.
    const pageViewData = {
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      is_white_label: isWhiteLabel
    };
    
    if (!isWhiteLabel) {
      logger.info('Page view tracked', {
        component: 'AnalyticsTracker',
        metadata: { path: location.pathname, ...pageViewData }
      });
    }
    
    trackPageView(location.pathname, pageViewData);
  }, [location.pathname, trackPageView, isWhiteLabel]);

  // Track critical user actions
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Track button clicks
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const buttonText = target.textContent || target.closest('button')?.textContent || 'Unknown';
        const trackingData = {
          action: 'button_click',
          target: buttonText,
          page: location.pathname
        };
        
        if (!isWhiteLabel) {
          logger.debug('Button click tracked', {
            component: 'AnalyticsTracker',
            metadata: trackingData
          });
        }
        
        trackEvent('user_action', trackingData);
      }
      
      // Track link clicks
      if (target.tagName === 'A' || target.closest('a')) {
        const link = target.closest('a');
        const trackingData = {
          action: 'link_click',
          target: link?.href || 'Unknown',
          text: link?.textContent || 'Unknown',
          page: location.pathname
        };
        
        if (!isWhiteLabel) {
          logger.debug('Link click tracked', {
            component: 'AnalyticsTracker',
            metadata: trackingData
          });
        }
        
        trackEvent('user_action', trackingData);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [location.pathname, trackEvent, isWhiteLabel]);

  return null;
};

// PagBankOAuthReturnBridge removed — replaced by synchronous IIFE at top of file

const LegacyCheckoutRoute = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("eventId") || searchParams.get("simulationId");

  if (!eventId) {
    return <Navigate to="/" replace />;
  }

  return <Navigate to={`/checkout/${eventId}${location.search}`} replace />;
};

const LegacyCheckoutConfirmationRoute = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("eventId") || searchParams.get("simulationId");

  if (!eventId) {
    return <Navigate to="/" replace />;
  }

  return <Navigate to={`/checkout/${eventId}/confirmacao${location.search}`} replace />;
};

const App = () => {
  return (
    <ErrorBoundary onError={(error, errorInfo) => {
      logger.error('Application Error Boundary triggered', error, {
        component: 'App',
        metadata: { errorInfo: errorInfo.componentStack }
      });
    }}>
      <FeatureDetector fallback={<SafeLoading size="lg" message="Verificando compatibilidade..." />}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <AuthProvider>
                <ClientPortalProvider>
                <BrowserRouter>
                <WhiteLabelBrandingProvider>
                <CustomDomainWrapper>
                <ErrorBoundary>
                  <WhiteLabelNoIndex />
                  <AnalyticsTracker />
                  
                  <ScrollToTop />
                  <Toaster />
                  <Sonner />
                  <FloatingCartButton />
                   {!isWhiteLabelHost() && <FloatingWhatsApp />}
                   <ViewTracker />
                   <Routes>
            <Route path="/" element={isWhiteLabelHost() ? <WhiteLabelLanding /> : <SupplierRegistration />} />
            <Route path="/cadastro-fornecedor" element={<SupplierRegistration />} />
            <Route path="/precos-e-planos" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <PricingPage />
              </Suspense>
            } />
            <Route path="/loja" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando loja..." />}>
                <LojaPage />
              </Suspense>
            } />
            
            <Route path="/login" element={<LoginPage />} />
            {/* Google Calendar OAuth callback */}
            <Route path="/auth/google-calendar/callback" element={
              <Suspense fallback={<SafeLoading size="lg" message="Processando autenticação..." />}>
                <GoogleCalendarCallback />
              </Suspense>
            } />
            <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
            <Route path="/redefinir-senha" element={<UpdatePasswordPage />} />
            <Route path="/nova-senha" element={<NovaSenhaPage />} />
            <Route path="/aceitar-convite" element={<AcceptCollaboratorInvite />} />

            <Route path="/infos-festa" element={<InfosFesta />} />
            
            {/* Public supplier store page */}
            <Route path="/loja/:supplierSlug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando loja..." />}>
                <SupplierStorePage />
              </Suspense>
            } />


            {/* Public shareable menu page */}
            <Route path="/cardapio/:supplierSlug/:productSlug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando cardápio..." />}>
                <SupplierMenuPage />
              </Suspense>
            } />
            
            <Route path="/checkout-changes/:eventId/:supplierId/:changeType" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando alterações..." />}>
                <CheckoutChangesPage />
              </Suspense>
            } />

            <Route path="/checkout" element={<LegacyCheckoutRoute />} />
            <Route path="/checkout/confirmacao" element={<LegacyCheckoutConfirmationRoute />} />

            <Route path="/checkout/:eventId" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando checkout..." />}>
                <CheckoutPage />
              </Suspense>
            } />
            <Route path="/checkout/:eventId/confirmacao" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando confirmação..." />}>
                <OrderConfirmationPage />
              </Suspense>
            } />
            <Route path="/checkout/:eventId/assinatura" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando contrato..." />}>
                <ContractSignaturePage />
              </Suspense>
            } />
            
            <Route path="/sobre-nos" element={<SobreNos />} />
            <Route path="/problema-solucao" element={<ProblemaSolucao />} />
            <Route path="/solucoes" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando soluções..." />}>
                <SolucoesPage />
              </Suspense>
            } />
            <Route path="/solucoes/:slug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando solução..." />}>
                <SolucaoPage />
              </Suspense>
            } />
            <Route path="/proposta-digital-buffet" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando proposta digital..." />}>
                <ExemploCardapioDigital />
              </Suspense>
            } />
            <Route path="/menu-digital-buffet" element={<Navigate to="/proposta-digital-buffet" replace />} />
            <Route path="/exemplo-cardapio-digital" element={<Navigate to="/proposta-digital-buffet" replace />} />
            <Route path="/materiais-gratuitos" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando materiais gratuitos..." />}>
                <MateriaisGratuitos />
              </Suspense>
            } />
            <Route path="/quiz-buffet" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando quiz..." />}>
                <MateriaisGratuitos />
              </Suspense>
            } />
            <Route path="/materiais-gratuitos/calculadora-preco" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando calculadora..." />}>
                <CalculadoraPrecoInsumos />
              </Suspense>
            } />
            <Route path="/materiais-gratuitos/calculadora-frete-eventos" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando calculadora..." />}>
                <CalculadoraFreteEventos />
              </Suspense>
            } />
            <Route path="/materiais-gratuitos/calculadora-bebidas-eventos" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando calculadora..." />}>
                <CalculadoraBebidasEventos />
              </Suspense>
            } />
            <Route path="/materiais-gratuitos/modelo-contrato-buffet" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando modelo..." />}>
                <ModeloContratoBuffet />
              </Suspense>
            } />
            <Route path="/materiais-gratuitos/reforma-tributaria-buffet" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando simulador..." />}>
                <ReformaTributariaBuffet />
              </Suspense>
            } />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
            {/* Blog routes */}
            <Route path="/agendar/:slug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando agenda..." />}>
                <AgendarReuniaoPage />
              </Suspense>
            } />
            <Route path="/blog" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando blog..." />}>
                <BlogPage />
              </Suspense>
            } />
            <Route path="/blog/:slug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando post..." />}>
                <BlogPostPage />
              </Suspense>
            } />
            {/* Supplier portal routes — path per sidebar section */}
            <Route path="/fornecedor/painel/*" element={
              <SupplierProtectedRoute>
                <Suspense fallback={<SafeLoading size="lg" message="Carregando dashboard do fornecedor..." />}>
                  <SupplierDashboard />
                </Suspense>
              </SupplierProtectedRoute>
            } />
            {/* Admin portal routes */}
            <Route path="/admin" element={
              <AdminProtectedRoute>
                <Suspense fallback={<SafeLoading size="lg" message="Carregando dashboard administrativo..." />}>
                  <AdminDashboard />
                </Suspense>
              </AdminProtectedRoute>
            } />
            {/* Partner portal */}
            <Route path="/parceiro/painel" element={
              <PartnerProtectedRoute>
                <Suspense fallback={<SafeLoading size="lg" message="Carregando painel do parceiro..." />}>
                  <PartnerDashboard />
                </Suspense>
              </PartnerProtectedRoute>
            } />
            <Route path="/demo/parceira" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando demonstração..." />}>
                <DemoParceiraLogin />
              </Suspense>
            } />
            {/* Client Portal Routes */}
            <Route path="/portal-cliente/:supplierSlug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando portal..." />}>
                <ClientPortalLoginPage />
              </Suspense>
            } />
            <Route path="/portal-cliente/:supplierSlug/meus-eventos" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando eventos..." />}>
                <ClientPortalEventsPage />
              </Suspense>
            } />
            {/* Payment success page */}
            <Route path="/pagamento-sucesso" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <PaymentSuccessPage />
              </Suspense>
            } />
            {/* PagBank OAuth callback */}
            <Route path="/auth/pagbank/callback" element={<PagBankCallbackPage />} />
            {/* Additional charge payment page */}
            <Route path="/pagamento-adicional/:token" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <AdditionalChargePage />
              </Suspense>
            } />
            {/* Installment payment page */}
            <Route path="/pagamento-parcela/:installmentId" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <InstallmentPaymentPage />
              </Suspense>
            } />
            {/* Short share link redirect */}
            <Route path="/s/:code" element={
              <Suspense fallback={<SafeLoading size="lg" message="Redirecionando..." />}>
                <ShareRedirectPage />
              </Suspense>
            } />
            {/* Passaporte do Evento: página do cliente com horários, cardápio, equipe e valores */}
            <Route path="/e/:token" element={
              <Suspense fallback={<SafeLoading size="lg" message="Abrindo o seu evento..." />}>
                <EventPassportPage />
              </Suspense>
            } />
            {/* Branded share link with supplier slug visible */}
            <Route path="/orcamento/:slug/:code" element={
              <Suspense fallback={<SafeLoading size="lg" message="Redirecionando..." />}>
                <ShareRedirectPage />
              </Suspense>
            } />
            {/* Short payment link redirect */}
            <Route path="/pay/:slug" element={
              <Suspense fallback={<SafeLoading size="lg" message="Redirecionando..." />}>
                <PaymentRedirectPage />
              </Suspense>
            } />
            {/* Unsubscribe page */}
            <Route path="/unsubscribe" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <UnsubscribePage />
              </Suspense>
            } />
            <Route path="/links" element={
              <Suspense fallback={<SafeLoading size="lg" message="Carregando..." />}>
                <LinksPage />
              </Suspense>
            } />
            {/* Link de campanha do vídeo do YouTube (Veiga BBQ): abre a página inicial da AmeUp
                instantaneamente, sem consulta ao banco, com marcas de origem para o GA4. */}
            <Route path="/veiga-bbq" element={
              <Navigate to="/?utm_source=youtube&utm_medium=video&utm_campaign=veiga-bbq" replace />
            } />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
                  </Routes>
                </ErrorBoundary>
                </CustomDomainWrapper>
                </WhiteLabelBrandingProvider>
                </BrowserRouter>
                </ClientPortalProvider>
              </AuthProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </FeatureDetector>
    </ErrorBoundary>
  );
};

export default App;
